import React, { useCallback, useLayoutEffect } from 'react';
import { Platform, View } from 'react-native';
import Animated from 'react-native-reanimated';
import Logo from 'design-system/Logo';
import Text from 'design-system/Text';
import Modal from 'utils/packages/Modal';
import CardRow from 'design-system/CardRow';
import InfoCard from 'design-system/InfoCard';
import BottomPanel from 'design-system/BottomPanel';
import SectionHeader from 'design-system/SectionHeader';
import IconCircle from 'design-system/icons/IconCircle';
import IconTransfer from 'design-system/icons/IconTransfer';
import CardRowContainer from 'design-system/CardRowContainer';
import CardRowTitleWithToolTip from 'design-system/CardRowTitleWithToolTip';
import { rem, SCREEN_BOTTOM_PADDING, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { AppView, Button, CardView, Separator, Spacer, withAnimatedNavigationHeader } from 'design-system';
import { formatAmount, getFractionDigits } from 'utils';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import Links, { knownLinks } from 'utils/links';
import createStyleSheets from 'utils/createStyleSheets';
import useOldFetch, { muteAllButThrows } from 'hooks/useFetch';
import { ThrowableFetchError } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import usePasscodeModal from 'hooks/usePasscodeModal';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { useStockMeta } from '../hooks';
import { commonToolTipStrings, SHARE_QUANTITY_DECIMALS } from '../constants';
import { isWeb } from '../../../constants';
import OrderSendingView from '../components/OrderSendingView';
import { cacheOrder, createAssetSwitchOrder, fetchTradingAccountInfoAfterOrder, updateWealthAccounts, } from '../actions';
import { formatNumberOfShares } from '../utils';
import { useNavigateBackAfterSellFromPortfolio } from '../hooks/useNavigateBackAfterSellFromPortfolio';
const SecclAssetSwitchOrderPreviewScreen = withAnimatedNavigationHeader(({ scrollHandler, route, navigation }) => {
    const { fromAssetId, toAssetId, orderPreviews, tradingAccountId } = route.params;
    const dispatch = useAppDispatch();
    const colors = useColors();
    const styles = useStyles(styleSet);
    const fromStock = useStockMeta(fromAssetId);
    const toStock = useStockMeta(toAssetId);
    const assetType = useAppSelector((store) => store.invest.caches[fromAssetId]?.type);
    const minimumTransferUnit = useAppSelector((store) => store.invest.caches[fromAssetId]?.minimumTransferUnit);
    const [getPin] = usePasscodeModal();
    const navigateBackAfterOrder = useNavigateBackAfterSellFromPortfolio();
    useLayoutEffect(() => {
        navigation.setOptions({
            headerRight: () => <HelpScreenButton />,
        });
    }, []);
    const { paddingHorizontalStyle } = useAppFrameDimensions(isWeb);
    const [, , , fetchOrder] = useOldFetch(muteAllButThrows);
    const onPressTAndC = useCallback(() => {
        if (Platform.OS === 'android') {
            navigation.navigate('Pdf', { url: knownLinks.investTerms });
        }
        else {
            Links.openLink(knownLinks.investTerms);
        }
    }, []);
    const { sellPreview, buyPreview } = orderPreviews;
    const min = assetType === 'ETF' ? 0.0001 : minimumTransferUnit;
    const maximumFractionDigits = min !== undefined ? getFractionDigits(min) : SHARE_QUANTITY_DECIMALS;
    const sellEstimated = sellPreview.order.estimated;
    const sellCommission = sellPreview.order.commission;
    const sellComputedQuantity = formatNumberOfShares(sellEstimated.quantity, false, undefined, undefined, maximumFractionDigits);
    const buyEstimated = buyPreview.order.estimated;
    const buyCommission = buyPreview.order.commission;
    const buyComputedQuantity = Math.round(buyEstimated.quantity * 10 ** 4) / 10 ** 4;
    const { ongoingFee, transactionFee, incidentalFee, platformFee, totalFee, illustrativeValue } = buyPreview.illustrationDetails || {};
    const updateStockData = useCallback(() => {
        dispatch(fetchTradingAccountInfoAfterOrder(2, tradingAccountId, fromAssetId));
    }, [fromAssetId, tradingAccountId]);
    const executePayment = useCallback(async (pin) => {
        Modal.hide();
        const content = <OrderSendingView />;
        Modal.show(content, {
            top: true,
            fullscreen: true,
            nonDismissable: true,
            minShowTime: 1500,
        });
        const { estimated: { quantity }, } = orderPreviews.sellPreview.order;
        const result = await fetchOrder(createAssetSwitchOrder(pin, quantity, fromAssetId, toAssetId, tradingAccountId));
        if (result.sellOrder) {
            dispatch(cacheOrder({ order: result.sellOrder, marketOpeningInfo: {} }, tradingAccountId));
            dispatch(updateWealthAccounts());
            updateStockData();
            Modal.hide(() => {
                navigateBackAfterOrder(3);
                navigation.navigate('InvestSwitchOrder', {
                    id: result.sellOrder.id,
                    status: result.sellOrder.status,
                    assetId: result.sellOrder.assetId,
                    switchOrderId: result.sellOrder.switchOrderId,
                    switchAssetId: result.sellOrder.switchAssetId,
                });
            }, { top: true });
        }
    }, [
        fetchOrder,
        fromAssetId,
        navigateBackAfterOrder,
        orderPreviews.sellPreview.order,
        toAssetId,
        tradingAccountId,
        updateStockData,
    ]);
    const handleContinue = useCallback(async (forcePinAuthentication) => {
        const { estimated: { quantity }, } = orderPreviews.sellPreview.order;
        const biometryDescription = `You are selling ${formatNumberOfShares(quantity)} of ${fromStock?.name ? fromStock.name : fromAssetId}`;
        try {
            const pin = await getPin({
                biometryDescription,
                title: 'Enter Your Pin Code',
                forcePinAuthentication: typeof forcePinAuthentication === 'boolean' ? forcePinAuthentication : false,
            });
            await executePayment(pin);
        }
        catch (e) {
            const fetchError = e instanceof ThrowableFetchError ? e : undefined;
            const errorToShow = fetchError
                ? fetchError.fetchError.errorMessage ?? 'This is awkward, please try again.'
                : 'This is awkward, please try again.';
            Modal.hide(() => {
                if (fetchError?.fetchError.errorType === 'PinError') {
                    handleContinue(true);
                }
                else {
                    Modal.showError(errorToShow);
                }
            }, { top: true });
        }
    }, [executePayment, fromAssetId, fromStock?.name, getPin, orderPreviews.sellPreview.order]);
    return (<AppView withFrame={false} style={styles.container}>
          <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={[paddingHorizontalStyle, styles.contentContainer]}>
            <View style={styles.headerContainer}>
              <View style={styles.headerRow}>
                <View style={styles.textContainer}>
                  <Text Text-24>Confirm your switch</Text>
                  <Spacer h={8}/>
                  <Text TextThin-16 Secondary>
                    Review the details of your sell and buy before confirming
                  </Text>
                </View>
                <Spacer w={16}/>
                <IconCircle width={44} height={44} color={colors.background.brandLight}>
                  <IconTransfer colorKey="brand"/>
                </IconCircle>
              </View>
            </View>

            <SectionHeader horizontalMargins largeVerticalMargins>
              Sell details
            </SectionHeader>
            <CardView noMarginTop noMarginBottom>
              <CardRowContainer>
                <CardRow small tableSpacing title="Name" info={fromStock?.name ? (<View style={styles.row}>
                        <View style={styles.stockNameContainer}>
                          <Text Text-14 style={styles.stockName}>
                            {fromStock?.name}
                          </Text>
                        </View>
                        <Spacer w={8}/>
                        <Logo R24 uri={fromStock?.iconUrl}/>
                      </View>) : undefined}/>
                <CardRow small tableSpacing title="Last close price" info={<Text Numbers-14>{formatAmount(sellEstimated.price.value, sellEstimated.price.currency, 2)}</Text>}/>
                <CardRow small tableSpacing title="No. of shares" info={<Text Numbers-14>{sellComputedQuantity}</Text>}/>
                <CardRow small tableSpacing title={<CardRowTitleWithToolTip title="Trading charge" toolTipTitle={commonToolTipStrings.tradingChargeTitle} toolTipDescription={commonToolTipStrings.tradingChargeDesc}/>} info={<Text Numbers-14>{formatAmount(sellCommission.value, sellCommission.currency, 2)}</Text>}/>
                <CardRow small tableSpacing title={<Text Text-14>Total amount (est.)</Text>} info={<Text Numbers-14>{formatAmount(sellEstimated.amount.value, sellEstimated.amount.currency, 2)}</Text>}/>
              </CardRowContainer>
            </CardView>

            <SectionHeader horizontalMargins largeVerticalMargins>
              Buy details
            </SectionHeader>
            <CardView noMarginTop noMarginBottom>
              <CardRowContainer>
                <CardRow small tableSpacing title="Name" info={toStock?.name ? (<View style={styles.row}>
                        <View style={styles.stockNameContainer}>
                          <Text Text-14 style={styles.stockName}>
                            {toStock?.name}
                          </Text>
                        </View>
                        <Spacer w={8}/>
                        <Logo R24 uri={toStock?.iconUrl}/>
                      </View>) : undefined}/>
                <CardRow small tableSpacing title="Last close price" info={<Text Numbers-14>{formatAmount(buyEstimated.price.value, buyEstimated.price.currency, 2)}</Text>}/>
                <CardRow small tableSpacing title={<CardRowTitleWithToolTip title={'No. of shares (est.)'} toolTipTitle={commonToolTipStrings.noOfSharesTitle} toolTipDescription={commonToolTipStrings.noOfSharesWithoutFxFeeDesc}/>} info={<Text Numbers-14>{buyComputedQuantity}</Text>}/>
                <CardRow small tableSpacing title={<CardRowTitleWithToolTip title="Trading charge" toolTipTitle={commonToolTipStrings.tradingChargeTitle} toolTipDescription={commonToolTipStrings.tradingChargeDesc}/>} info={<Text Numbers-14>{formatAmount(buyCommission.value, buyCommission.currency, 2)}</Text>}/>
                <CardRow small tableSpacing title={<Text Text-14>Total cost (est.)</Text>} info={<Text Numbers-14>{formatAmount(buyEstimated.amount.value, buyEstimated.amount.currency, 2)}</Text>}/>
              </CardRowContainer>
            </CardView>

            {buyPreview.illustrationDetails ? (<>
                <SectionHeader horizontalMargins largeVerticalMargins>
                  How much might this cost
                </SectionHeader>
                <CardView noMarginTop noMarginBottom>
                  <View style={styles.descriptionContainer}>
                    <Text TextThin-14 Secondary>
                      If you have this investment for 5 years, you can expect the following charges over that 5 years,
                      assuming 5% annual growth.
                    </Text>
                  </View>
                  <Separator />
                  <CardRowContainer>
                    <CardRow small tableSpacing title={<CardRowTitleWithToolTip title="Ongoing charges figure" toolTipTitle={commonToolTipStrings.ongoingChargeTitle} toolTipDescription={commonToolTipStrings.ongoingChargeDesc}/>} info={<Text Numbers-14>{formatAmount(ongoingFee, buyEstimated.price.currency, 2)}</Text>}/>
                    <CardRow small tableSpacing title={<CardRowTitleWithToolTip title="Transaction cost (est.)" toolTipTitle={commonToolTipStrings.transactionCostTitle} toolTipDescription={commonToolTipStrings.transactionCostDesc}/>} info={<Text Numbers-14>{formatAmount(transactionFee, buyEstimated.price.currency, 2)}</Text>}/>
                    <CardRow small tableSpacing title={<CardRowTitleWithToolTip title="Incidental cost (est.)" toolTipTitle={commonToolTipStrings.incidentalCostTitle} toolTipDescription={commonToolTipStrings.incidentalCostDesc}/>} info={<Text Numbers-14>{formatAmount(incidentalFee, buyEstimated.price.currency, 2)}</Text>}/>
                    <CardRow small tableSpacing title={<CardRowTitleWithToolTip title="AUM fee" toolTipTitle={commonToolTipStrings.platformFeeTitle} toolTipDescription={commonToolTipStrings.platformFeeDesc}/>} info={<Text Numbers-14>{formatAmount(platformFee, buyEstimated.price.currency, 2)}</Text>}/>
                    <CardRow small tableSpacing title="Total costs (est.)" info={<Text Numbers-14>{formatAmount(totalFee, 'GBP', 2)}</Text>}/>
                    <CardRow small tableSpacing title={<Text Text-14>Illustrative 5 year value</Text>} info={<Text Numbers-14>{formatAmount(illustrativeValue, buyEstimated.price.currency, 2)}</Text>}/>
                  </CardRowContainer>
                </CardView>
                <Spacer h={16}/>
                <InfoCard>
                  <Text TextThin-14 Secondary>
                    Costs and returns are illustrative and not guaranteed. Check investment charges before investing.
                    Figures may vary due to rounding or compounding. Your capital is at risk.
                  </Text>
                </InfoCard>
              </>) : null}
          </Animated.ScrollView>
          <BottomPanel>
            <Text TextThin-14 Secondary centered>
              Our{' '}
              <Text Text-14 Primary suppressHighlighting onPress={onPressTAndC}>
                T&Cs
              </Text>{' '}
              apply to this transaction
            </Text>
            <Spacer h={16}/>
            <Button preventDoubleTap brand title="Confirm" onPress={handleContinue}/>
          </BottomPanel>
        </AppView>);
}, ({ colors }) => ({
    title: '',
    headerColorOutputRange: [colors.background.dark, colors.cards.onDark],
}));
const styleSet = createStyleSheets(() => ({
    container: {
        flex: 1,
    },
    headerContainer: {
        paddingTop: rem(8),
        paddingHorizontal: rem(16),
    },
    descriptionContainer: {
        padding: rem(16),
    },
    contentContainer: {
        paddingBottom: SCREEN_BOTTOM_PADDING,
    },
    row: {
        flexDirection: 'row',
        alignItems: 'center',
    },
    headerRow: {
        flexDirection: 'row',
    },
    textContainer: {
        flex: 1,
    },
    stockNameContainer: {
        flexShrink: 1,
        alignItems: 'flex-end',
    },
    stockName: {
        textAlign: 'right',
    },
}));
export default SecclAssetSwitchOrderPreviewScreen;
